import axios from 'axios'
import Router from "../router";
import { Auth } from "../utils/auth";
import { apiCache } from "../utils/cache";

const instance = axios.create({
    baseURL: Router.apiBaseUrl,
});

// Interceptor de request - agregar headers de autenticación
instance.interceptors.request.use(
    (config) => {
        let auth = new Auth();
        const token = auth.getToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        let language = localStorage.getItem('lang')
        config.headers['Accept-Language'] = language || 'es'

        // Agregar timestamp para evitar cache del navegador en requests GET
        if (config.method === 'get') {
          config.params = {
            ...config.params,
            _t: Date.now()
          };
        }

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Interceptor de response - cachear respuestas exitosas
instance.interceptors.response.use(
    async (response) => {
        // Cachear respuestas GET exitosas para uso offline
        if (response.config.method === 'get' && response.status === 200) {
            try {
                const method = response.config.method;
                const url = response.config.url;
                const params = response.config.params;

                // 10 minutos para respuestas de API por defecto
                let ttl = 10 * 60 * 1000;

                // Si es flujo Loto (ID 1075), guardar por 24 horas
                if (url.includes('api/workpermit/WorkflowData/Paginated') && params?.workflowId == 1075) {
                    ttl = 24 * 60 * 60 * 1000;
                }

                // Remover timestamp (_t) de los params para el cache
                // Esto asegura que la key del cache sea consistente
                const cacheParams = { ...params };
                delete cacheParams._t;

                await apiCache.set(method, url, cacheParams, response, ttl);
            } catch (cacheError) {
                console.warn('Error cacheando respuesta:', cacheError);
            }
        }

        return response;
    },
    async (error) => {
        // Si hay error de conexión (network error), intentar usar cache
        const isNetworkError = !error.response || 
                              error.code === 'NETWORK_ERROR' || 
                              error.message?.includes('Network Error') || 
                              error.message?.includes('Failed to fetch') ||
                              !navigator.onLine;

        if (isNetworkError) {
            try {
                const config = error.config;
                if (!config) return Promise.reject(error);

                const method = config.method || 'get';
                const url = config.url;
                const params = config.params;

                // Remover timestamp (_t) de los params para buscar en cache
                const cacheParams = { ...params };
                delete cacheParams._t;

                const cachedResponse = await apiCache.get(method, url, cacheParams);

                if (cachedResponse) {
                    // Retornar respuesta del cache en el formato esperado por axios
                    return {
                        data: cachedResponse.data,
                        status: cachedResponse.status || 200,
                        statusText: 'OK (cached)',
                        headers: cachedResponse.headers || {},
                        config: config,
                        cached: true,
                        timestamp: cachedResponse.timestamp,
                        request: config
                    };
                } else {
                    console.warn('❌ No hay respuesta en cache para:', `${method}:${url}`);
                }
            } catch (cacheError) {
                console.error('Error obteniendo del cache:', cacheError);
            }
        }

        return Promise.reject(error);
    }
);

export default instance;